import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API from '../../api';

// redux
import { mapDispatchProps, mapStateToProps } from '../../store/store';
import { connect } from 'react-redux';

import TextInputField from '../../components/textInput';
import Button from '../../components/button';
import Divider from '../../components/divider';
import { X } from 'react-feather';
import { isEmpty } from '../../helpers/object';
import BRAND from '../../constants/brand';
import { useLanguage } from '@languages/index';
import Select from 'components/select';
import { COUNTRY_CD } from 'constants/country';
import { queries } from 'api/api.constants';

const AlarmIndex = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.settings };
  const queryClient = useQueryClient();

  const [focusedInputIndex, setFocusedInputIndex] = useState();
  const isWholesale = props.brand.businessType === BRAND.BUSINESS_TYPE.WHOLESALE;

  const countryCodes = [
    { name: `${languageTexts.korea} (+${COUNTRY_CD.KR})`, value: COUNTRY_CD.KR },
    { name: `${languageTexts.japan} (+${COUNTRY_CD.JP})`, value: COUNTRY_CD.JP },
  ];

  const fetchBrand = () => {
    API.Brand.show({ brandToken: props.auth.token })
      .then((response) => {
        const data = response.data.brand;

        data.members.forEach((member) => {
          if (member.mobile != null && /^\d{10,11}$/.test(member.mobile)) {
            const [, mobileLeft, mobileMiddle, mobileRight] = /^(\d{3})(\d{3,4})(\d{4})$/.exec(member.mobile);
            member.mobileLeft = mobileLeft;
            member.mobileMiddle = mobileMiddle;
            member.mobileRight = mobileRight;
          } else {
            member.mobileLeft = languageTexts.mobile_left;
            member.mobileMiddle = '';
            member.mobileRight = '';
          }

          if (member.countryCode) {
            const selected = countryCodes.filter((duration) => {
              return duration.value == member.countryCode;
            })[0];

            member.selectedCountryCode = selected;
          }
        });

        if (!response.data.brand.members.length) {
          data.members = [
            {
              countryCode: languageTexts.country_code,
              selectedCountryCode: countryCodes.filter((duration) => {
                return duration.value == languageTexts.country_code;
              })[0],
              mobileLeft: languageTexts.mobile_left,
              mobileMiddle: '',
              mobileRight: '',
              role: isWholesale ? languageTexts.wholesale_manager : '',
              receiveNewsletter: false,
              receiveInstagramLink: false,
              receiveAmbassadorApplyMsg: true,
              receiveRentalBudgetWarningMsg: false,
            },
          ];
        }

        setBrand(data);
      })
      .catch((error) => {
        props.showModal({
          text: languageTexts.alarm_text_1,
        });
      });
  };

  const handleMemberInput = async (target, index, value) => {
    if (target === 'mobileLeft' && !/^\d{0,3}$/.test(value)) {
      return;
    } else if ((target === 'mobileMiddle' || target === 'mobileRight') && !/^\d{0,4}$/.test(value)) {
      return;
    }

    const formCopy = { ...brand };

    formCopy['members'][index][target] = value;

    formCopy['members'][index]['mobile'] =
      `${formCopy['members'][index]['mobileLeft']}` +
      `${formCopy['members'][index]['mobileMiddle']}` +
      `${formCopy['members'][index]['mobileRight']}`;

    setFocusedInputIndex(index);

    setBrand((prevState) => {
      return { prevState, ...formCopy };
    });
  };

  const handleBrandInput = async (target, value) => {
    const formCopy = { ...brand };
    formCopy[`${target}`] = value;
    await setBrand(formCopy);
  };

  const [brand, setBrand] = useState<any>(() => {
    fetchBrand();
  });

  const renderPageTitle = () => {
    return (
      <>
        <div className="text-2xl font-bold tracking-tighter flex justify-center mt-12">
          {languageTexts.alarm_text_2}
        </div>

        <div className="mt-9">
          <Divider />
        </div>
      </>
    );
  };

  const renderForm = () => {
    const renderAddMemberButton = () => {
      return (
        <Button
          className="border-1 border-gray-200 hover:border-black text-center py-2"
          justify={'center'}
          text={'+'}
          onClick={() => {
            setBrand({
              ...brand,
              members: [
                ...(brand?.members ?? []),
                {
                  receiveNewsletter: false,
                  receiveInstagramLink: false,
                  receiveAmbassadorApplyMsg: true,
                  receiveRentalBudgetWarningMsg: false,
                  countryCode: languageTexts.country_code,
                  selectedCountryCode: countryCodes.filter((duration) => {
                    return duration.value == languageTexts.country_code;
                  })[0],
                  mobileLeft: languageTexts.mobile_left,
                  mobileMiddle: '',
                  mobileRight: '',
                  role: isWholesale ? languageTexts.alarm_text_13 : '',
                },
              ],
            });
          }}
        />
      );
    };

    const renderMemberForm = () => {
      const isWholesale = props.brand.businessType === BRAND.BUSINESS_TYPE.WHOLESALE;

      return (
        brand &&
        !isEmpty(brand) &&
        brand.members.map((member: any, index: number) => {
          return (
            <div className="flex flex-col gap-5" key={`member=input-${index}`}>
              {index === 0 ? null : (
                <div
                  className="flex flex-row cursor-pointer"
                  onClick={() => {
                    setBrand({
                      ...brand,
                      members: [
                        ...brand.members.filter((member, memberIndex) => {
                          return memberIndex != index;
                        }),
                      ],
                    });
                  }}
                >
                  <div className="ml-auto text-white bg-black p-1">
                    <X size={24} />
                  </div>
                </div>
              )}

              <div className="flex flex-row gap-3">
                <TextInputField
                  bordered
                  className="text-sm"
                  value={member?.name ?? ''}
                  placeholder={languageTexts.name}
                  isInputFocused={focusedInputIndex == index}
                  onChangeText={(value) => {
                    handleMemberInput('name', index, value);
                  }}
                />

                <TextInputField
                  bordered
                  className="text-sm"
                  value={member?.role ?? ''}
                  placeholder={languageTexts.role}
                  onChangeText={(value) => {
                    handleMemberInput('role', index, value);
                  }}
                />
              </div>

              <div className="flex flex-row gap-3">
                <Select
                  bordered
                  options={countryCodes}
                  selected={member?.selectedCountryCode?.name ?? ''}
                  value={member?.selectedCountryCode?.name ?? ''}
                  onChange={(selectedName) => {
                    const selected = countryCodes.filter((duration) => {
                      return duration.name == selectedName;
                    })[0];

                    handleMemberInput('selectedCountryCode', index, selected);
                    handleMemberInput('countryCode', index, selected.value);

                    console.log('selected', selected);
                  }}
                />
              </div>
              <div className="flex flex-row gap-3 items-center">
                <TextInputField
                  bordered
                  className="text-sm"
                  value={member?.mobileLeft ?? ''}
                  placeholder={languageTexts.mobile_left_placeholder}
                  onChangeText={(value) => {
                    handleMemberInput('mobileLeft', index, value);
                  }}
                />
                <span>-</span>
                <TextInputField
                  bordered
                  className="text-sm"
                  value={member?.mobileMiddle ?? ''}
                  placeholder={''}
                  onChangeText={(value) => {
                    handleMemberInput('mobileMiddle', index, value);
                  }}
                />
                <span>-</span>
                <TextInputField
                  bordered
                  value={member?.mobileRight ?? ''}
                  placeholder={''}
                  onChangeText={(value) => {
                    handleMemberInput('mobileRight', index, value);
                  }}
                />
              </div>
              <div className="flex flex-col gap-3">
                {/* <Checkbox
                  textClassName="text-sm color-[#999] tracking-tighter"
                  text={languageTexts.alarm_text_3}
                  checked={member?.receiveNewsletter}
                  onClick={(value) => {
                    handleMemberInput("receiveNewsletter", index, value)
                  }}
                />
                {!isWholesale && (
                  <Checkbox
                    textClassName="text-sm color-[#999] tracking-tighter"
                    text={languageTexts.alarm_text_4}
                    checked={member?.receiveInstagramLink}
                    onClick={(value) => {
                      handleMemberInput("receiveInstagramLink", index, value)
                    }}
                  />
                )} */}
                {!isWholesale && (
                  <>
                    {/* <Checkbox
                      textClassName="text-sm color-[#999] tracking-tighter"
                      text={languageTexts.alarm_text_5}
                      checked={member?.receiveAmbassadorApplyMsg}
                      onClick={(value) => {
                        handleMemberInput(
                          "receiveAmbassadorApplyMsg",
                          index,
                          value
                        );
                      }}
                    /> */}

                    {/* <Checkbox
                      textClassName="text-sm color-[#999] tracking-tighter"
                      text={languageTexts.alarm_text_6}
                      checked={member?.receiveRentalBudgetWarningMsg}
                      onClick={(value) => {
                        handleMemberInput("receiveRentalBudgetWarningMsg", index, value)
                      }}
                    /> */}
                  </>
                )}
              </div>
              <Divider />
            </div>
          );
        })
      );
    };

    return (
      <div className="flex flex-col my-10">
        {isWholesale == false ? (
          <>
            <div className="mobile-form">
              <div className="text-lg font-bold tracking-tighter">{languageTexts.alarm_text_7}</div>

              <div className="form-input mt-4">
                <TextInputField
                  className="text-sm"
                  bordered
                  value={brand?.email ?? ''}
                  placeholder={'username@email.com'}
                  onChangeText={(value) => {
                    handleBrandInput(`email`, value);
                  }}
                />
              </div>
            </div>

            <div className="my-7">
              <Divider />
            </div>
          </>
        ) : null}

        <div className="contact-form">
          {isWholesale ? (
            <div className="text-lg font-bold tracking-tighter">{languageTexts.alarm_text_12}</div>
          ) : (
            <div className="text-lg font-bold tracking-tighter">{languageTexts.alarm_text_9}</div>
          )}

          <div className="text-base tracking-tighter text-[#999] mt-2">{languageTexts.alarm_text_10}</div>
          <div className="form-input flex flex-col gap-5 mt-4 mb-7">{renderMemberForm()}</div>
          <div className="mt-3">{renderAddMemberButton()}</div>
        </div>

        <div className="form-submit-button mt-3">
          <Button
            textColor={'WHITE'}
            buttonColor={'BLACK'}
            text={languageTexts.save2}
            onClick={() => {
              let params = {};

              params['email'] = brand.email;
              params['members'] = [...brand.members];

              if (params['members'] && params['members'].length > 0) {
                for (let member of params['members']) {
                  if (!member['role']) {
                    props.showModal({
                      text: languageTexts.alarm_text_14,
                    });
                    return false;
                  }

                  if (!member['mobileLeft']) {
                    member['mobileLeft'] = languageTexts.mobile_left;
                  }

                  if (member['mobileLeft'].length && member['mobileLeft'].length < 2) {
                    console.log(1);
                    props.showModal({
                      text: languageTexts.alarm_text_15,
                    });
                    return false;
                  }
                  if (!member['mobileMiddle'] || member['mobileMiddle'].length != 4) {
                    console.log(2);
                    props.showModal({
                      text: languageTexts.alarm_text_15,
                    });
                    return false;
                  }
                  if (!member['mobileRight'] || member['mobileRight'].length != 4) {
                    console.log(3);
                    props.showModal({
                      text: languageTexts.alarm_text_15,
                    });
                    return false;
                  }
                }
              }

              API.Brand.update({
                brandToken: props.auth.token,
                ...params,
              })
                .then((response) => {
                  let arr = {
                    text: response.data.message,
                  };

                  if (response.data.title.length) {
                    arr['title'] = response.data.title;
                  }
                  props.showModal(arr);
                })
                .catch((error) => {
                  props.showModal({
                    title: languageTexts.alarm_text_11,
                    text: JSON.stringify(error, undefined, 2),
                  });
                })
                .finally(() => {
                  fetchBrand();
                  queryClient.invalidateQueries({
                    queryKey: queries.brand.getBrand._def,
                  });
                  props.onUpdate ? props.onUpdate() : null;
                });
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto w-full" style={{ maxWidth: '385px' }}>
      <div className="px-5 mx-auto w-full">
        {renderPageTitle()}
        {renderForm()}
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(AlarmIndex);
